import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import axiosInstance from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import { openScopedAccount } from "../../utils/accountContext";
import { useClearScopedAccountOnMount } from "../../utils/useClearScopedAccountOnMount";
import { formatDisplayDate } from "../../utils/formatDisplayDate";

const detailStyles = `
.accounts-page {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 6.5rem);
}
.cad-hero {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem 1.25rem;
  padding: 1.15rem 1.25rem;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #eef0f2;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  margin-bottom: 1rem;
}
.cad-hero__identity {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 0;
  flex: 1 1 240px;
}
.cad-hero__avatar {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #0f766e;
  background: #ccfbf1;
  border: 1px solid #99f6e4;
  flex-shrink: 0;
}
.cad-hero__title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #212529;
  line-height: 1.25;
  word-break: break-word;
}
.cad-hero__meta {
  margin: 0.2rem 0 0;
  font-size: 0.875rem;
  color: #6c757d;
  word-break: break-all;
}
.cad-hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  align-items: center;
  flex-shrink: 0;
}
.cad-hero__action {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 36px;
  padding: 0.4rem 0.9rem !important;
  border-radius: 10px !important;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  text-decoration: none !important;
  box-shadow: none !important;
  vertical-align: middle;
}
.cad-hero__action i {
  margin: 0 !important;
  line-height: 1;
  width: 1em;
  text-align: center;
}
.cad-hero__action:focus,
.cad-hero__action:focus-visible {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(20, 184, 166, 0.25) !important;
}
.cad-hero__action--primary {
  color: #fff !important;
  background: #0d9488 !important;
  border: 1px solid #0d9488 !important;
}
.cad-hero__action--primary:hover {
  background: #0f766e !important;
  border-color: #0f766e !important;
  color: #fff !important;
}
.cad-hero__action--secondary {
  color: #495057 !important;
  background: #fff !important;
  border: 1px solid #ced4da !important;
}
.cad-hero__action--secondary:hover {
  background: #f8f9fa !important;
  border-color: #adb5bd !important;
  color: #212529 !important;
}
.cad-grid {
  display: grid;
  gap: 1rem;
}
@media (min-width: 992px) {
  .cad-grid {
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
    align-items: start;
  }
}
.cad-card {
  border: 1px solid #eef0f2;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  overflow: hidden;
}
.cad-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #eef0f2;
  background: #fafbfc;
}
.cad-card__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #212529;
}
.cad-card__head p {
  margin: 0.2rem 0 0;
  font-size: 0.8rem;
  color: #6c757d;
  max-width: 36rem;
}
.cad-card__body {
  padding: 1.25rem;
}
.cad-card__footer {
  padding: 0.9rem 1.25rem;
  border-top: 1px solid #eef0f2;
  background: #fafbfc;
  display: flex;
  justify-content: flex-end;
}
.cad-field-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.35rem;
}
.cad-input {
  border-radius: 10px;
  border-color: #dee2e6;
}
.cad-input:focus {
  border-color: #14b8a6;
  box-shadow: 0 0 0 0.2rem rgba(20, 184, 166, 0.15);
}
.cad-metrics {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem;
}
@media (max-width: 575.98px) {
  .cad-metrics {
    grid-template-columns: 1fr;
  }
}
.cad-metric {
  border: 1px solid #eef0f2;
  border-radius: 12px;
  padding: 0.85rem 0.95rem;
  background: #f8fafc;
  min-height: 74px;
}
.cad-metric--wide {
  grid-column: 1 / -1;
}
.cad-metric__label {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 0.25rem;
}
.cad-metric__value {
  font-size: 1rem;
  font-weight: 650;
  color: #212529;
  line-height: 1.3;
}
.cad-note {
  margin: 0 0 1rem;
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  background: #f0fdfa;
  border: 1px solid #99f6e4;
  color: #0f766e;
  font-size: 0.8rem;
  line-height: 1.4;
}
.cad-billing-actions {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}
.cad-billing-actions .btn {
  border-radius: 10px;
  font-weight: 600;
  justify-content: center;
}
.cad-billing-secondary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
}
@media (max-width: 575.98px) {
  .cad-billing-secondary {
    grid-template-columns: 1fr;
  }
}
.cad-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 650;
  line-height: 1.2;
  border: 1px solid transparent;
}
.cad-status-pill--on {
  color: #0f766e;
  background: #ccfbf1;
  border-color: #99f6e4;
}
.cad-status-pill--off {
  color: #b45309;
  background: #fef3c7;
  border-color: #fde68a;
}
.cad-status-pill--active {
  color: #198754;
  background: #d1e7dd;
  border-color: #a3cfbb;
}
.cad-status-pill--inactive {
  color: #b45309;
  background: #fef3c7;
  border-color: #fde68a;
}
.cad-status-toggle {
  background: #f8fafc;
  border: 1px solid #eef0f2;
}

body.dark-mode .cad-hero,
body.dark-mode .cad-card {
  background: #171d26;
  border-color: #2d3848;
  box-shadow: none;
}
body.dark-mode .cad-hero__title,
body.dark-mode .cad-card__head h2,
body.dark-mode .cad-metric__value,
body.dark-mode .cad-field-label {
  color: #f3f4f6;
}
body.dark-mode .cad-hero__meta,
body.dark-mode .cad-card__head p,
body.dark-mode .cad-metric__label {
  color: #9ca3af;
}
body.dark-mode .cad-hero__avatar {
  color: #2dd4bf;
  background: rgba(45, 212, 191, 0.12);
  border-color: rgba(45, 212, 191, 0.35);
}
body.dark-mode .cad-hero__action--secondary {
  color: #e5e7eb !important;
  background: #1f2733 !important;
  border-color: #3d4b5f !important;
}
body.dark-mode .cad-hero__action--secondary:hover {
  background: #263041 !important;
  border-color: #4b5c73 !important;
  color: #f3f4f6 !important;
}
body.dark-mode .cad-card__head,
body.dark-mode .cad-card__footer {
  background: #1f2733;
  border-color: #2d3848;
}
body.dark-mode .cad-input {
  background: #1f2733;
  border-color: #2d3848;
  color: #f3f4f6;
}
body.dark-mode .cad-input:disabled,
body.dark-mode .cad-input[readonly] {
  background: #171d26;
  color: #9ca3af;
}
body.dark-mode .cad-metric {
  background: #1f2733;
  border-color: #2d3848;
}
body.dark-mode .cad-note {
  background: rgba(45, 212, 191, 0.1);
  border-color: rgba(45, 212, 191, 0.3);
  color: #5eead4;
}
body.dark-mode .cad-status-toggle {
  background: #1f2733;
  border-color: #2d3848;
}
`;

function initialsFromName(name) {
  if (!name || typeof name !== "string") return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const CompanyAccountDetail = () => {
  useClearScopedAccountOnMount();
  const { id } = useParams();
  const history = useHistory();
  const [fisher, setFisher] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [billingAction, setBillingAction] = useState(null);
  const [detaching, setDetaching] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [fisherRes, packagesRes] = await Promise.all([
          axiosInstance.get(`api/company/accounts/${id}`),
          axiosInstance.get("api/Packages/GetAll"),
        ]);
        const f = fisherRes.data;
        f.EndDate = f.EndDate ? new Date(f.EndDate).toISOString().split("T")[0] : "";
        f.PackageID = f.PackageID ?? "";
        f.LicensedPondCount = f.LicensedPondCount ?? 1;
        setFisher(f);
        const pkgRows = Array.isArray(packagesRes.data)
          ? packagesRes.data
          : packagesRes.data?.data ?? [];
        setPackages(pkgRows);
      } catch (error) {
        showErrorToast(error.response?.data?.message || "Failed to load account");
        history.push("/company/accounts");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, history]);

  const packageName = useMemo(() => {
    if (!fisher) return "—";
    if (fisher.packageName) return fisher.packageName;
    if (fisher.package?.Name) return fisher.package.Name;
    if (fisher.PackageName) return fisher.PackageName;
    const match = packages.find((p) => Number(p.id) === Number(fisher.PackageID));
    if (match?.Name) return match.Name;
    if (fisher.EndDate && !fisher.PackageID) return "Trial";
    return "—";
  }, [fisher, packages]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFisher((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axiosInstance.post(`api/company/accounts/${id}/update`, {
        FullName: fisher.FullName,
        Status: fisher.Status,
      });
      setFisher((prev) => ({
        ...prev,
        ...res.data,
        EndDate: res.data.EndDate
          ? new Date(res.data.EndDate).toISOString().split("T")[0]
          : "",
      }));
      showSuccessToast("Account updated");
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAccount = () => {
    openScopedAccount(
      {
        id: fisher.id || id,
        name: fisher.FullName,
        email: fisher.Email,
      },
      { history }
    );
  };

  const hasStripeSubscription = !!(
    fisher?.stripeSubscriptionId || fisher?.StripeSubscriptionId
  );
  const cancelAtPeriodEnd = !!(
    fisher?.stripeCancelAtPeriodEnd || fisher?.StripeCancelAtPeriodEnd
  );
  const isStripeTrialing =
    String(fisher?.recurringBilling?.status || "").toLowerCase() === "trialing";

  const handleCancelSubscription = async () => {
    const confirm = await Swal.fire({
      title: isStripeTrialing ? "Cancel trial?" : "Cancel subscription?",
      text: isStripeTrialing
        ? "Cancelling during the trial stops future billing and removes package access for this account."
        : "This turns off auto-renewal. The account keeps access until the current subscription end date.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: isStripeTrialing ? "Cancel trial" : "Cancel subscription",
      cancelButtonText: "Keep subscription",
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;

    setBillingAction("cancel");
    try {
      const { data } = await axiosInstance.post(
        `api/company/accounts/${id}/cancel-stripe-renewal`
      );
      showSuccessToast(data.message || "Subscription cancelled");
      if (data.trialCancelled) {
        setFisher((prev) => ({
          ...prev,
          PackageID: null,
          packageName: null,
          EndDate: data.EndDate
            ? new Date(data.EndDate).toISOString().split("T")[0]
            : prev.EndDate,
          stripeSubscriptionId: null,
          StripeSubscriptionId: null,
          stripeCancelAtPeriodEnd: false,
          StripeCancelAtPeriodEnd: false,
          recurringBilling: null,
        }));
        return;
      }
      setFisher((prev) => ({
        ...prev,
        stripeCancelAtPeriodEnd: true,
        StripeCancelAtPeriodEnd: true,
        EndDate: data.EndDate
          ? new Date(data.EndDate).toISOString().split("T")[0]
          : prev.EndDate,
        recurringBilling: {
          ...prev?.recurringBilling,
          cancelAtPeriodEnd: true,
        },
      }));
    } catch (error) {
      showErrorToast(
        error.response?.data?.message || "Could not cancel subscription"
      );
    } finally {
      setBillingAction(null);
    }
  };

  const handleResumeSubscription = async () => {
    setBillingAction("resume");
    try {
      const { data } = await axiosInstance.post(
        `api/company/accounts/${id}/resume-stripe-renewal`
      );
      showSuccessToast(data.message || "Auto-renewal resumed");
      setFisher((prev) => ({
        ...prev,
        stripeCancelAtPeriodEnd: false,
        StripeCancelAtPeriodEnd: false,
        recurringBilling: {
          ...prev?.recurringBilling,
          cancelAtPeriodEnd: false,
        },
      }));
    } catch (error) {
      showErrorToast(
        error.response?.data?.message || "Could not resume auto-renewal"
      );
    } finally {
      setBillingAction(null);
    }
  };

  const handleDetach = async () => {
    if (detaching || billingAction) return;
    const confirm = await Swal.fire({
      title: "Remove from consultancy?",
      html: `<p class="mb-2">Remove <strong>${fisher.FullName || "this account"}</strong> from your consultancy?</p>
        <p class="mb-0 text-muted small">They keep their ponds and data, and will manage their own subscription going forward. Company billing for this account will stop.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d9488",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, remove from consultancy",
      cancelButtonText: "Cancel",
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;

    setDetaching(true);
    try {
      const res = await axiosInstance.post(`api/company/accounts/${id}/detach`);
      showSuccessToast(
        res.data?.message ||
          "Account removed from consultancy. They now manage their own subscription."
      );
      history.push("/company/accounts");
    } catch (error) {
      showErrorToast(
        error.response?.data?.message || "Failed to remove account from consultancy"
      );
    } finally {
      setDetaching(false);
    }
  };

  if (loading || !fisher) return <Preloader />;

  const isActive = !!fisher.Status;
  const isSandbox = fisher.IsCompanySandbox === true || fisher.IsCompanySandbox === 1;
  const pondCount = fisher.LicensedPondCount ?? 1;
  const initials = initialsFromName(fisher.FullName);

  return (
    <div className="accounts-page">
      <style>{detailStyles}</style>

      <ul className="breadcrumb mb-3">
        <li className="breadcrumb-item">
          <Link to="/">Home</Link>
        </li>
        <li className="breadcrumb-item">
          <Link to="/company/accounts">Accounts</Link>
        </li>
        <li className="breadcrumb-item active">{fisher.FullName || "Account"}</li>
      </ul>

      <header className="cad-hero">
        <div className="cad-hero__identity">
          <div className="cad-hero__avatar" aria-hidden="true">
            {initials}
          </div>
          <div className="min-w-0">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h1 className="cad-hero__title">{fisher.FullName || "Pond owner account"}</h1>
              {isSandbox ? (
                <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 rounded-pill">
                  Demo
                </span>
              ) : null}
              <span
                className={`cad-status-pill ${
                  isActive ? "cad-status-pill--active" : "cad-status-pill--inactive"
                }`}
              >
                <i className="fa fa-circle" style={{ fontSize: 7 }} aria-hidden />
                {isActive ? "Active" : "Inactive"}
              </span>
            </div>
            <p className="cad-hero__meta">{fisher.Email}</p>
          </div>
        </div>
        <div className="cad-hero__actions">
          <button
            type="button"
            className="cad-hero__action cad-hero__action--primary"
            onClick={handleOpenAccount}
            disabled={detaching}
          >
            <i className="fa fa-sign-in" aria-hidden="true" />
            <span>Open account</span>
          </button>
          <Link
            to={`/company/staff/${id}`}
            className="cad-hero__action cad-hero__action--secondary"
          >
            <i className="fa fa-users" aria-hidden="true" />
            <span>Staff</span>
          </Link>
          {!isSandbox ? (
            <button
              type="button"
              className="cad-hero__action cad-hero__action--secondary"
              onClick={handleDetach}
              disabled={detaching || !!billingAction}
              title="Remove from consultancy"
            >
              <i className={`fa ${detaching ? "fa-spinner fa-spin" : "fa-unlink"}`} aria-hidden="true" />
              <span>{detaching ? "Removing…" : "Remove from consultancy"}</span>
            </button>
          ) : null}
        </div>
      </header>

      <div className="cad-grid">
        <form onSubmit={handleSave} className="cad-card">
          <div className="cad-card__head">
            <div>
              <h2>Profile</h2>
              <p>Update the owner name and whether this account can sign in.</p>
            </div>
          </div>
          <div className="cad-card__body">
            <div className="mb-3">
              <label className="cad-field-label" htmlFor="companyAccountFullName">
                Full name
              </label>
              <input
                id="companyAccountFullName"
                className="form-control cad-input"
                name="FullName"
                value={fisher.FullName || ""}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="cad-field-label" htmlFor="companyAccountEmail">
                Email
              </label>
              <input
                id="companyAccountEmail"
                className="form-control cad-input"
                value={fisher.Email || ""}
                disabled
                readOnly
              />
              <div className="form-text mt-1">Login email cannot be changed here.</div>
            </div>
            <div className="d-flex align-items-center justify-content-between gap-3 rounded-3 px-3 py-2 cad-status-toggle">
              <div>
                <div className="fw-semibold" style={{ fontSize: "0.9rem" }}>
                  Account active
                </div>
                <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                  Inactive owners cannot sign in to PondStat.
                </div>
              </div>
              <div className="form-check form-switch m-0">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="statusCheck"
                  name="Status"
                  checked={isActive}
                  onChange={handleChange}
                  style={{ width: "2.4em", height: "1.25em", cursor: "pointer" }}
                />
                <label className="visually-hidden" htmlFor="statusCheck">
                  Account active
                </label>
              </div>
            </div>
          </div>
          <div className="cad-card__footer">
            <button type="submit" className="btn btn-primary px-4" disabled={saving}>
              {saving ? "Saving…" : "Save profile"}
            </button>
          </div>
        </form>

        <section className="cad-card">
          <div className="cad-card__head">
            <div>
              <h2>Subscription & ponds</h2>
              <p>Billing for this owner runs through your company checkout.</p>
            </div>
          </div>
          <div className="cad-card__body">
            <p className="cad-note">
              <i className="fa fa-info-circle me-1" aria-hidden="true" />
              Package details are updated when you buy ponds or renew — not by editing fields on this page.
            </p>

            <div className="cad-metrics mb-3">
              <div className="cad-metric cad-metric--wide">
                <span className="cad-metric__label">Package</span>
                <div className="cad-metric__value d-flex align-items-center flex-wrap gap-2">
                  <span>{packageName}</span>
                  {isStripeTrialing ? (
                    <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">
                      Stripe trial
                      {fisher?.recurringBilling?.trialEnd
                        ? ` · ends ${formatDisplayDate(fisher.recurringBilling.trialEnd)}`
                        : ""}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="cad-metric">
                <span className="cad-metric__label">Licensed ponds</span>
                <div className="cad-metric__value">
                  {pondCount} {pondCount === 1 ? "pond" : "ponds"}
                </div>
              </div>
              <div className="cad-metric">
                <span className="cad-metric__label">Ends</span>
                <div className="cad-metric__value">
                  {formatDisplayDate(fisher.EndDate) || "—"}
                </div>
              </div>
              {hasStripeSubscription && (
                <div className="cad-metric cad-metric--wide">
                  <span className="cad-metric__label">Auto-renewal</span>
                  <div className="cad-metric__value d-flex align-items-center gap-2 flex-wrap">
                    {cancelAtPeriodEnd ? (
                      <span className="cad-status-pill cad-status-pill--off">
                        Off — access until end date
                      </span>
                    ) : (
                      <span className="cad-status-pill cad-status-pill--on">On</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="cad-billing-actions">
              <Link
                to={`/company/accounts/${id}/extra-pond`}
                className="btn btn-primary d-inline-flex align-items-center"
              >
                <i className="fa fa-plus me-2" aria-hidden="true" />
                Buy extra ponds
              </Link>
              <div className="cad-billing-secondary">
                <Link
                  to={`/company/accounts/${id}/renew`}
                  className="btn btn-outline-primary d-inline-flex align-items-center"
                >
                  <i className="fa fa-sync me-2" aria-hidden="true" />
                  Renew / change
                </Link>
                {hasStripeSubscription &&
                  (cancelAtPeriodEnd ? (
                    <button
                      type="button"
                      className="btn btn-outline-success"
                      disabled={billingAction === "resume"}
                      onClick={handleResumeSubscription}
                    >
                      {billingAction === "resume" ? "Resuming…" : "Resume auto-renewal"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      disabled={billingAction === "cancel"}
                      onClick={handleCancelSubscription}
                    >
                      {billingAction === "cancel"
                        ? "Cancelling…"
                        : isStripeTrialing
                          ? "Cancel trial"
                          : "Cancel subscription"}
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CompanyAccountDetail;
